interface BootstrapPopoverInstance {
    update?: () => void;
    hide?: () => void;
}

interface BootstrapPopoverConstructor {
    new (element: Element, options?: Record<string, unknown>): BootstrapPopoverInstance;
    getInstance?: (element: Element) => BootstrapPopoverInstance | null;
}

interface BootstrapPopoverApi {
    Popover?: BootstrapPopoverConstructor;
}

type PopperConfig = Record<string, unknown>;

/** Room kept free above a basket dot for the sticky site header before the popover flips below the dot (px). */
const HEADER_RESERVE = 110;
/** Gap between the popover and the dot's click area, and between the popover and the window edge (px). */
const GAP = 12;
const EDGE = 8;

/** Mobile view (same breakpoint as the theme's mobile CSS): popovers open below their trigger there. */
const isMobileView = (): boolean => window.matchMedia?.('(max-width: 767.98px)').matches ?? false;

/**
 * Popovers of the availability display: the info icon next to the PDP traffic light and the
 * traffic light dot of each basket position (both marked data-woehner-popover). Bootstrap popovers are opt-in, so they
 * are initialised here: hover and keyboard focus on desktop, tap (focus) on touch devices; the
 * trigger is an <a tabindex="0"> because Safari doesn't focus buttons on tap.
 */
export function initAvailabilityInfo(): void {
    const popover = (window as unknown as { bootstrap?: BootstrapPopoverApi }).bootstrap?.Popover;
    if (!popover) {
        return;
    }

    // Devices without hover (phones, tablets): open on tap. Bootstrap's 'hover focus' does not toggle on a second tap and its
    // emulated mouse events leave popovers hanging; 'click' toggles reliably. Desktop keeps hover and keyboard focus.
    const trigger = window.matchMedia?.('(hover: none)').matches ? 'click' : 'hover focus';

    const elements = Array.from(document.querySelectorAll('[data-woehner-popover]'));
    elements.forEach((element) => {
        if (element.classList.contains('woehner-avail__hit')) {
            initBasketDot(popover, element, trigger);
        } else {
            initInfoIcon(popover, element, trigger);
        }
    });

    if (elements.length > 0) {
        initDismissal(popover, elements);
    }
}

/**
 * Close an open popover without Bootstrap's own trigger. hide() alone leaves Bootstrap's internal "trigger is active"
 * flags set, so with the 'click' trigger the next tap would be read as "close" and the popover needed two taps to open
 * again; reset them as well.
 */
function dismiss(popover: BootstrapPopoverConstructor, element: Element): void {
    const instance = popover.getInstance?.(element);
    if (!instance) {
        return;
    }
    const flags = (instance as unknown as { _activeTrigger?: Record<string, boolean> })._activeTrigger;
    if (flags) {
        Object.keys(flags).forEach((key) => {
            flags[key] = false;
        });
    }
    instance.hide?.();
}

/**
 * Close on a tap/click outside (the 'click' trigger does not do that itself; also closes the first popover when a second
 * trigger is tapped, so never two are open) and on Escape (WCAG 1.4.13); focus stays on the trigger. pointerdown is used
 * for the outside check because it fires for every tap, also on iOS Safari where 'click' does not reach document for
 * non-interactive targets.
 */
function initDismissal(popover: BootstrapPopoverConstructor, elements: Element[]): void {
    const isOpen = (element: Element): boolean => element.hasAttribute('aria-describedby');

    document.addEventListener('pointerdown', (event) => {
        const target = event.target as Node | null;
        elements.forEach((element) => {
            if (!isOpen(element) || (target && element.contains(target))) {
                return;
            }
            const tip = document.getElementById(element.getAttribute('aria-describedby') ?? '');
            if (tip && target && tip.contains(target)) {
                return;
            }
            dismiss(popover, element);
        });
    });

    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') {
            return;
        }
        elements.forEach((element) => {
            if (isOpen(element)) {
                dismiss(popover, element);
            }
        });
    });
}

/**
 * PDP info icon: Bootstrap/Popper placement as usual (popover inserted right after the icon, default absolute strategy).
 * fallbackPlacements: Bootstrap's default order is top, right, bottom, left; a tall popover that does not fit above would
 * be pushed to the side and centred vertically on the icon, so only top <-> bottom is allowed.
 */
function initInfoIcon(popover: BootstrapPopoverConstructor, element: Element, trigger: string): void {
    // Keep EDGE px between the popover and the window edge (Popper's preventOverflow has no padding by default, so a
    // popover that is wider than the room right of the icon would sit flush against the screen edge). Same-named
    // modifiers are merged by Popper, so Bootstrap's own preventOverflow options (boundary) stay.
    const popperConfig = (defaults: PopperConfig): PopperConfig => ({
        ...defaults,
        modifiers: [...((defaults.modifiers as unknown[] | undefined) ?? []), { name: 'preventOverflow', options: { padding: EDGE } }],
    });

    // Mobile view: below the icon (the finger and the sticky header cover the area above), top as the fallback.
    const mobile = isMobileView();
    const instance = new popover(element, {
        customClass: 'woehner-avail-popover',
        trigger,
        offset: [0, 8],
        placement: mobile ? 'bottom' : 'top',
        fallbackPlacements: [mobile ? 'top' : 'bottom'],
        popperConfig,
    });

    // Popper measures the popover when it is inserted. If it is not fully laid out at that moment it is placed as if it
    // were shorter and then grows away from the icon. Re-measure once it is shown and on the next frame. In a short window
    // (landscape phone) the popover fits neither above nor below the icon: cap its body height to the larger side first.
    const reposition = (): void => {
        const tip = document.getElementById(element.getAttribute('aria-describedby') ?? '');
        if (tip) {
            fitHeight(tip, element);
        }
        instance.update?.();
    };
    element.addEventListener('inserted.bs.popover', () => window.requestAnimationFrame(reposition));
    element.addEventListener('shown.bs.popover', reposition);
}

/**
 * Cap the popover body (it scrolls inside) when the popover is taller than the larger of the free areas above and below
 * the anchor; the arrow sits outside the body, so it stays visible. Without a cap the variable is removed.
 */
function fitHeight(tip: HTMLElement, anchor: Element): void {
    tip.style.removeProperty('--woehner-avail-body-max');
    const rect = anchor.getBoundingClientRect();
    const height = tip.getBoundingClientRect().height;
    const room = Math.max(rect.top - HEADER_RESERVE - GAP, window.innerHeight - rect.bottom - EDGE - GAP);
    if (room < height) {
        tip.style.setProperty('--woehner-avail-body-max', `${Math.max(96, Math.floor(room - 24))}px`);
    }
}

/**
 * Basket dot: the popover is positioned by CSS relative to the dot, not by Popper. On the real basket page Popper's
 * containing-block maths was off by the scroll offset (<body> is position:relative with min-height:100vh), whatever
 * strategy was used. Popper is kept for the Bootstrap lifecycle (events, aria-describedby, sanitizer, hover/focus) but its
 * style writers are switched off. The popover is appended to the dot's parent .basket-item__avail, which is the
 * positioning anchor (position:relative, as wide as the dot); the CSS puts it above the dot, centred. Here we only choose
 * above/below depending on the room and keep it inside the window horizontally.
 */
function initBasketDot(popover: BootstrapPopoverConstructor, element: Element, trigger: string): void {
    const popperConfig = (defaults: PopperConfig): PopperConfig => ({
        ...defaults,
        modifiers: [
            ...((defaults.modifiers as unknown[] | undefined) ?? []),
            { name: 'applyStyles', enabled: false },
            { name: 'computeStyles', enabled: false },
        ],
    });

    new popover(element, {
        customClass: 'woehner-avail-popover',
        trigger,
        // Bootstrap 5.2 treats container:false as "append to <body>"; the wrapper is the anchor, so pass it explicitly.
        container: element.parentElement ?? 'body',
        popperConfig,
    });

    const place = (): void => {
        const tipId = element.getAttribute('aria-describedby');
        const tip = tipId ? document.getElementById(tipId) : null;
        if (!tip) {
            return;
        }
        tip.classList.add('woehner-avail-popover--anchored');

        // Measure above, centred, without shift and without a height cap.
        tip.classList.remove('woehner-avail-popover--below');
        tip.style.removeProperty('--woehner-avail-shift');
        tip.style.removeProperty('--woehner-avail-body-max');
        const dot = element.getBoundingClientRect();
        const height = tip.getBoundingClientRect().height;
        const roomAbove = dot.top - HEADER_RESERVE - GAP;
        const roomBelow = window.innerHeight - dot.bottom - EDGE - GAP;
        // Mobile view: below the dot by default (the finger covers the area above), above when it does not fit below but
        // fits above. Desktop: above by default. When the preferred side is too small, take the other one if it fits, else
        // the side with more room and let the body scroll (landscape phones: a tall popover fits neither side).
        let below = isMobileView();
        if ((below ? roomBelow : roomAbove) < height) {
            below = roomBelow >= height || (roomAbove < height && roomBelow > roomAbove);
        }
        if (below) {
            tip.classList.add('woehner-avail-popover--below');
        }
        const room = below ? roomBelow : roomAbove;
        if (room < height) {
            tip.style.setProperty('--woehner-avail-body-max', `${Math.max(96, Math.floor(room - 24))}px`);
        }

        // Keep it inside the window and inside .page-layout__main (it clips horizontally, see the CSS) horizontally; the
        // arrow stays on the dot (CSS compensates with the same variable).
        const rect = tip.getBoundingClientRect();
        const main = tip.closest('.page-layout__main')?.getBoundingClientRect();
        const minLeft = Math.max(EDGE, main?.left ?? 0);
        const maxRight = Math.min(document.documentElement.clientWidth - EDGE, main?.right ?? Infinity);
        let shift = 0;
        if (rect.left < minLeft) {
            shift = minLeft - rect.left;
        } else if (rect.right > maxRight) {
            shift = maxRight - rect.right;
        }
        const limit = Math.max(0, rect.width / 2 - 16);
        shift = Math.max(-limit, Math.min(limit, shift));
        if (shift !== 0) {
            tip.style.setProperty('--woehner-avail-shift', `${Math.round(shift)}px`);
        }
    };

    element.addEventListener('inserted.bs.popover', place);
    element.addEventListener('shown.bs.popover', place);
}
